/**
 * ParentThreadView — single thread + composer.
 *
 * Reads the thread via `api-get-parent-thread` (this ticket). Sends
 * replies via `api-send-parent-message` (#PL-EF-MSG) with a
 * client-side 2000-char cap mirroring the server-side check.
 *
 * Per the issue:
 *   - "Messages here are not for emergencies" disclosure pinned at
 *     the top of the thread.
 *   - Message list is chronological (oldest first), so the
 *     conversation reads top-to-bottom in natural order.
 *   - Composer is plain text, 2000 char cap, with a live counter.
 *   - The thread is marked read on the parent's side (via
 *     api-mark-parent-message-read) when the parent opens the view,
 *     so the unread badge in the dashboard clears.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../../lib/supabase/client';
import { useParentAuth } from '../../lib/auth/useParentAuth';
import type { ParentMessageRow, ParentThreadRow } from './parentTypes';

interface Props {
  threadId: string;
  onBack: () => void;
}

const MAX_BODY_LENGTH = 2000;

function generateUUID(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers / non-secure contexts — never return a
  // constant UUID, or `client_message_id` would collide across replies
  // (the edge function dedupes on it) and React `key`s would clash on
  // optimistic local messages.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function formatBubbleTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function ParentThreadView({ threadId, onBack }: Props): ReactElement {
  const { user } = useParentAuth();
  const [thread, setThread] = useState<ParentThreadRow | null>(null);
  const [messages, setMessages] = useState<ParentMessageRow[]>([]);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      const { data, error: threadErr } = await supabase.functions.invoke<{
        thread: ParentThreadRow;
        messages: ParentMessageRow[];
      }>('api-get-parent-thread', { body: { thread_id: threadId } });
      if (cancelled) return;
      if (threadErr) {
        setError(threadErr.message);
        setLoading(false);
        return;
      }
      setThread(data?.thread ?? null);
      setMessages(data?.messages ?? []);
      setLoading(false);

      // Mark the parent's side of any unread messages as read so the
      // dashboard's unread badge clears.
      const unread = (data?.messages ?? []).filter(
        (m) => m.read_by_parent_at === null,
      );
      const now = new Date().toISOString();
      for (const m of unread) {
        try {
          await supabase.functions.invoke(
            'api-mark-parent-message-read',
            { body: { message_id: m.id } },
          );
          if (cancelled) return;
          setMessages((prev) =>
            prev.map((row) =>
              row.id === m.id ? { ...row, read_by_parent_at: now } : row,
            ),
          );
        } catch {
          // Best-effort: a single failed mark shouldn't disable the UI.
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [threadId]);

  async function handleSend(): Promise<void> {
    const body = replyText.trim();
    if (body.length === 0) return;
    if (body.length > MAX_BODY_LENGTH) return;
    setSending(true);
    setError(null);
    try {
      const { data, error: sendErr } = await supabase.functions.invoke<{
        message: ParentMessageRow;
      }>('api-send-parent-message', {
        body: {
          thread_id: threadId,
          body,
          client_message_id: generateUUID(),
        },
      });
      if (sendErr) {
        setError(sendErr.message);
        return;
      }
      const sent = data?.message ?? null;
      if (sent) {
        setMessages((prev) => [...prev, sent]);
      } else {
        // Optimistic fallback if the server didn't echo a row.
        const optimistic: ParentMessageRow = {
          id: generateUUID(),
          thread_id: threadId,
          sender_user_id: user?.id ?? '',
          body,
          sent_at: new Date().toISOString(),
          read_by_parent_at: new Date().toISOString(),
          read_by_clinician_at: null,
          client_message_id: generateUUID(),
        };
        setMessages((prev) => [...prev, optimistic]);
      }
      setReplyText('');
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return (
      <main className="screen parent-thread">
        <button type="button" className="link-button" onClick={onBack}>
          ← Back to dashboard
        </button>
        <p className="tagline">Loading conversation…</p>
      </main>
    );
  }

  if (error || !thread) {
    return (
      <main className="screen parent-thread">
        <button type="button" className="link-button" onClick={onBack}>
          ← Back to dashboard
        </button>
        <p className="error">{error ?? 'Could not load this conversation.'}</p>
      </main>
    );
  }

  const remaining = MAX_BODY_LENGTH - replyText.length;
  const isOverLimit = remaining < 0;
  const canSend = !sending
    && replyText.trim().length > 0
    && !isOverLimit;

  return (
    <main className="screen parent-thread">
      <button type="button" className="link-button" onClick={onBack}>
        ← Back to dashboard
      </button>

      <section
        className="card emergency-disclosure"
        role="note"
        aria-label="Not for emergencies disclosure"
      >
        <strong>Messages here are not for emergencies.</strong>{' '}
        If your kid needs urgent help, call your SLP or 911 directly.
      </section>

      <div className="thread-list" aria-label="Conversation">
        {messages.length === 0 && (
          <p className="tagline">
            No messages yet. Send the first message to start the
            conversation with your SLP.
          </p>
        )}
        {messages.map((m) => {
          const isMe = m.sender_user_id === user?.id;
          return (
            <article
              key={m.id}
              className={
                isMe ? 'bubble bubble-mine' : 'bubble bubble-theirs'
              }
            >
              <p>{m.body}</p>
              <p className="tagline">{formatBubbleTime(m.sent_at)}</p>
            </article>
          );
        })}
      </div>

      <form
        className="composer"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSend();
        }}
      >
        <label className="visually-hidden" htmlFor="parent-thread-reply">
          Reply
        </label>
        <textarea
          id="parent-thread-reply"
          rows={3}
          maxLength={MAX_BODY_LENGTH}
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          placeholder="Type a message…"
          disabled={sending}
        />
        <div className="composer-actions">
          <span
            className={
              isOverLimit
                ? 'counter counter-over'
                : 'counter'
            }
            aria-live="polite"
          >
            {replyText.length}/{MAX_BODY_LENGTH}
          </span>
          <button
            type="submit"
            className="snap-button"
            disabled={!canSend}
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
        {error && <p className="error">{error}</p>}
      </form>
    </main>
  );
}
